import { Fragment, useState } from 'react';

import { Divider } from '@mui/material';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';

import DataLoader from '@webclient/components/data-loader';
import EllipsisText from '@webclient/components/ellipsis-typography';
import CategorySelector from '@webclient/components/search-forms/gamification-search/actions/bots-selector/sections/category-selector';
import StyledInput from '@webclient/components/search-forms/gamification-search/actions/bots-selector/sections/list/styles';
import {
  StyledBox,
  StyledCategoryStack,
  StyledImageBot,
  StyledListItem,
  StyledOnlineDot,
  StyledStackOptions,
  StyledStackOptionsItem,
  StyledText,
} from '@webclient/components/search-forms/gamification-search/actions/bots-selector/styles';

import calculateFullName from '@webclient/lib/calculateFullName';
import CONFIG from '@webclient/lib/const/bots';
import createPublicImageUrl, {
  retrieveImageWithFallback,
} from '@webclient/lib/createPublicImageUrl';
import getAvatar from '@webclient/lib/getAvatar';

import useAppDispatch from '@webclient/hooks/useAppDispatch';
import useAppSelector from '@webclient/hooks/useAppSelector';
import useGetBots from '@webclient/hooks/useGetBots';

import { setBotByUserId } from '@webclient/state/slices/app-settings';
import { selectBot } from '@webclient/state/slices/app-settings/userSettingSelector';
import { updateResultWithBot } from '@webclient/state/slices/search-session';

import { AllBotsCategoryType } from '@webclient/shared-types/bots';
import { MinimalBotType } from '@webclient/shared-types/search-result/streaming';

import CrossIcon from '~icons/knowz-iconify/cross';
import SearchIcon from '~icons/knowz-iconify/search';

import { IconButton, Text } from '@knowz/knowz-ui';
import { useTranslate } from '@tolgee/react';

const BOTS_AVATAR_FALLBACK =
  'https://asset.knowz.com/assets/images/not-found-characters-avatar-%5Boptimised%5D.webp';

type Props = {
  onBotSwitch?: (bot: MinimalBotType) => void;
  onClose: () => void;
};

export default function List({ onBotSwitch, onClose }: Props) {
  const { t } = useTranslate();
  const [category, setCategory] = useState<AllBotsCategoryType>('All');
  const bot = useAppSelector(selectBot);
  const me = useAppSelector((state) => state.auth.me);
  const dispatch = useAppDispatch();
  const [filteredValue, setFilteredValue] = useState<string>('');
  const {
    data: botsData,
    isLoading: isBotsDataLoading,
    isError: isBotsDataError,
  } = useGetBots({
    category:
      category === 'All' || category === 'Knowz AI' ? undefined : category,
    shouldPin: true,
    filteredValue,
  });

  function handleFilterByCategory(category: AllBotsCategoryType) {
    setCategory(category);
  }

  function handleValueOnChange(
    event?: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) {
    setFilteredValue(event?.target.value ?? '');
  }

  if (isBotsDataError) {
    return (
      <Alert severity="error">
        {t('response.errors.code.shortCode.520', 'An error occurred')}
      </Alert>
    );
  }

  return (
    <>
      <Stack
        direction="row"
        sx={{ justifyContent: 'space-between' }}
      >
        <Stack>
          <Text
            variant="textXl"
            weight="semibold"
          >
            {t(
              'layout.searchWidget.botsSelector.list.title',
              'Change your bot',
            )}
          </Text>
          <Text
            variant="textSm"
            color="textTertiary"
          >
            {t(
              'layout.searchWidget.botsSelector.list.subtitle',
              'Select a new bot anytime to continue this search',
            )}
          </Text>
        </Stack>
        <IconButton
          onClick={onClose}
          variant="ghost"
          size="small"
          color="secondary"
          shape="fullRounded"
        >
          <CrossIcon />
        </IconButton>
      </Stack>
      <StyledInput
        autoFocus
        startAdornment={<SearchIcon />}
        disabled={isBotsDataLoading}
        onChange={handleValueOnChange}
        placeholder={t(
          'layout.searchWidget.botsSelector.placeholder',
          'Find the personality that suites you',
        )}
        endAdornment={
          <CategorySelector
            currentSelectedCategory={category}
            handleFilterByCategory={handleFilterByCategory}
          />
        }
      />

      <DataLoader
        loading={{
          description: 'Loading bots data',
          isLoading: isBotsDataLoading,
        }}
        data={{
          emptyMessage: 'No bots found',
          length: botsData?.data.length,
        }}
        error={{
          isError: isBotsDataError,
          title: 'Error',
        }}
      >
        <Stack gap={3}>
          {botsData?.data.map((b, index) => {
            const selected = b.id === bot.id;
            const isItKnowzAI = b.id === CONFIG.DEFAULT_KNOWZ_AI_BOT.id;
            const isItMadeByKnowz = b.isDefaultOwner;

            return (
              <Fragment key={`${b.id}-${index}`}>
                <StyledListItem
                  disableGutters
                  disablePadding
                  onClick={() => {
                    onBotSwitch?.({
                      id: b.id,
                      name: b.name,
                      image: b.image,
                    });
                    dispatch(
                      setBotByUserId({
                        userId: me!.uuid,
                        bot: {
                          id: b.id,
                          name: b.name,
                          image: b.image,
                        },
                      }),
                    );
                    dispatch(
                      updateResultWithBot({
                        id: b.id,
                        name: b.name,
                        image: b.image,
                      }),
                    );
                  }}
                  selected={selected}
                >
                  <StyledStackOptions>
                    <StyledBox>
                      {selected && <StyledOnlineDot />}
                      <StyledImageBot
                        src={
                          isItKnowzAI
                            ? CONFIG.DEFAULT_KNOWZ_AI_BOT.avatar
                            : retrieveImageWithFallback(
                                createPublicImageUrl(getAvatar(b.image)),
                                BOTS_AVATAR_FALLBACK,
                              )
                        }
                        alt={b.name}
                      />
                    </StyledBox>
                    <StyledStackOptionsItem sx={{ width: 330 }}>
                      <Stack
                        direction="row"
                        gap={3}
                      >
                        <EllipsisText
                          lineClamp={1}
                          variant="textMd"
                          weight="medium"
                        >
                          {b.name}
                        </EllipsisText>
                        <StyledCategoryStack>
                          {CONFIG.CATEGORY_BY_ICONS[b.category]}
                        </StyledCategoryStack>
                      </Stack>
                      <EllipsisText
                        variant="textSm"
                        lineClamp={1}
                      >
                        {b.description}
                      </EllipsisText>
                      <StyledText
                        variant="textXs"
                        sx={{ mt: 2 }}
                      >
                        {isItMadeByKnowz
                          ? 'By Knowz.com'
                          : `By ${calculateFullName({ firstName: b.owner?.firstName, lastName: b.owner?.lastName })}`}
                      </StyledText>
                    </StyledStackOptionsItem>
                  </StyledStackOptions>
                </StyledListItem>
                <Divider />
              </Fragment>
            );
          })}
        </Stack>
      </DataLoader>
    </>
  );
}
